import { ALL_FLEET_MISSIONS, FLEET_MISSION_DEFINITIONS, GUEST_UPGRADE_MISSION_LOCK_DAYS, GUEST_UPGRADE_MISSION_LOCK_MS, } from "@kaeliux/shared";
/**
 * Combines the stored guest history with whether the current request comes
 * from a guest. `patch` is what the server should merge into the player doc,
 * or `null` when nothing changed. A former guest whose registration hasn't
 * been stamped yet counts as registered `now`, so the wait can never be
 * skipped by racing the stamp. An account that turns back into a guest (by
 * unlinking every sign-in method) loses its stamp, so a wait served before
 * can't be banked for the next registration.
 */
export function resolveAccountStanding(record, guest, nowMs) {
    if (guest) {
        const patch = {
            ...(record?.startedAsGuest ? {} : { startedAsGuest: true }),
            ...(record?.guestUpgradedAt !== undefined ? { guestUpgradedAt: null } : {}),
        };
        return { standing: { guest: true }, patch: Object.keys(patch).length > 0 ? patch : null };
    }
    if (!record?.startedAsGuest)
        return { standing: { guest: false }, patch: null };
    if (record.guestUpgradedAt === undefined) {
        return { standing: { guest: false, registeredAt: nowMs }, patch: { guestUpgradedAt: nowMs } };
    }
    return { standing: { guest: false, registeredAt: record.guestUpgradedAt }, patch: null };
}
/**
 * The guest history a player's first planet in a new universe starts with:
 * the guest history is kept per universe, so a former guest who registered
 * elsewhere brings their earliest registration time along instead of
 * starting afresh with no wait. `null` when no universe saw them as a guest.
 */
export function carryOverGuestAccount(records, nowMs) {
    const guestRecords = records.filter((r) => r.startedAsGuest);
    if (guestRecords.length === 0)
        return null;
    const stamps = guestRecords.map((r) => r.guestUpgradedAt ?? nowMs);
    return { startedAsGuest: true, guestUpgradedAt: Math.min(...stamps) };
}
export function getMissionAccountLock(mission, standing, nowMs) {
    const def = FLEET_MISSION_DEFINITIONS[mission];
    if (standing.guest)
        return def.allowedForGuests ? null : { reason: "guest" };
    if (!def.lockedAfterGuestUpgrade || standing.registeredAt === undefined)
        return null;
    const unlocksAt = standing.registeredAt + GUEST_UPGRADE_MISSION_LOCK_MS;
    return nowMs < unlocksAt ? { reason: "newAccount", unlocksAt } : null;
}
function plural(count, unit) {
    return `${count} ${unit}${count === 1 ? "" : "s"}`;
}
/** A remaining wait in words, rounded up to the minute, e.g. "2 days 5 hours" or "5 hours 30 minutes". */
export function formatAccountLockWait(ms) {
    const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0)
        return hours > 0 ? `${plural(days, "day")} ${plural(hours, "hour")}` : plural(days, "day");
    if (hours > 0)
        return minutes > 0 ? `${plural(hours, "hour")} ${plural(minutes, "minute")}` : plural(hours, "hour");
    return plural(minutes, "minute");
}
const GUEST_MISSION_NAMES = ALL_FLEET_MISSIONS
    .filter((m) => FLEET_MISSION_DEFINITIONS[m].allowedForGuests)
    .map((m) => FLEET_MISSION_DEFINITIONS[m].name)
    .join(" and ");
export function describeMissionAccountLock(mission, lock, nowMs) {
    const name = FLEET_MISSION_DEFINITIONS[mission].name;
    if (lock.reason === "guest") {
        return `Guest accounts can only send ${GUEST_MISSION_NAMES} flights. Create an account to send ${name} flights.`;
    }
    return `${name} flights unlock ${GUEST_UPGRADE_MISSION_LOCK_DAYS} days after a guest account is registered. `
        + `Available in ${formatAccountLockWait(lock.unlocksAt - nowMs)}.`;
}
//# sourceMappingURL=accountAccess.js.map